import { useMemo, useState } from 'react';
import { SKILLS } from '@crossmatch/shared';
import { skillLabel } from '../lib/format.js';
import { SKILL_CATEGORY_LABELS, SKILL_CATEGORY_ORDER } from '../lib/labels.js';

export interface SkillPickerProps {
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  hint?: string;
  /** 上限（与后端 validateProfile 的「技能最多 20 个」一致） */
  max?: number;
}

/**
 * 技能多选：数据全部来自 shared 的 SKILLS（id 稳定、中文名展示、别名可搜）。
 * 只能选已收录技能 —— 未收录词不允许在这里"偷偷加进来"。
 */
export function SkillPicker({ selected, onChange, label = '技能（多选）', hint, max = 20 }: SkillPickerProps) {
  const [keyword, setKeyword] = useState('');

  const groups = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    const filtered =
      term.length === 0
        ? SKILLS
        : SKILLS.filter(
            (skill) =>
              skill.name.toLowerCase().includes(term) ||
              skill.id.toLowerCase().includes(term) ||
              (skill.aliases ?? []).some((alias) => alias.toLowerCase().includes(term)),
          );
    return SKILL_CATEGORY_ORDER.map((category) => ({
      category,
      items: filtered.filter((skill) => skill.category === category),
    })).filter((group) => group.items.length > 0);
  }, [keyword]);

  const atLimit = selected.length >= max;

  const toggle = (id: string): void => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id));
      return;
    }
    if (atLimit) return;
    onChange([...selected, id]);
  };

  return (
    <div className="skill-picker">
      <div className="row">
        <span className="field-label">{label}</span>
        <span className={atLimit ? 'field-hint right' : 'muted small right'}>
          已选 {selected.length}/{max}
        </span>
      </div>
      {hint !== undefined ? <span className="field-hint">{hint}</span> : null}

      <input
        className="input"
        type="search"
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="搜索技能：中文名 / 英文名 / 别名（如 前端、ps、figma）"
        aria-label="搜索技能"
      />

      {selected.length > 0 ? (
        <div className="selected-chips">
          {selected.map((id) => (
            <button key={id} type="button" className="chip on" onClick={() => toggle(id)} title="点击移除该技能">
              {skillLabel(id)} <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="field-hint">还没选技能。至少选 1 个（后端会校验：技能必填、最多 {max} 个）。</p>
      )}

      <div className="skill-groups">
        {groups.length === 0 ? (
          <p className="field-hint">
            词表里没有匹配「{keyword}」的技能。请换关键词；确实没有的，属于「未收录」—— 未收录词不会计入技能，也不能参与匹配计分。
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.category}>
              <div className="skill-group-title">{SKILL_CATEGORY_LABELS[group.category]}</div>
              <div className="chip-grid">
                {group.items.map((skill) => {
                  const on = selected.includes(skill.id);
                  return (
                    <button
                      key={skill.id}
                      type="button"
                      className={on ? 'chip on' : 'chip'}
                      aria-pressed={on}
                      disabled={!on && atLimit}
                      onClick={() => toggle(skill.id)}
                      title={(skill.aliases ?? []).join(' / ')}
                    >
                      {skill.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {atLimit ? <span className="field-hint">已达上限 {max} 个，先移除一个再加。</span> : null}
    </div>
  );
}